import { useEffect, useState, useRef } from 'react';
import { parse, isValid, format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import Header from '../components/header';
import Sidebar from '../components/sidebar';
import NestedAccordion from './ledgerAccordian';
import axios from 'axios';
import * as XLSX from "xlsx";

function App() {

    const dropAreaRef = useRef(null);
    const fileInputRef = useRef(null);
    const fileNameRef = useRef(null);
    const formRef = useRef(null);

    const [uploading, setUploading] = useState(false);
    const [getData, setGetData] = useState(null);
    const [edit, setEdit] = useState(null);
    const [openLedger, setOpenLedger] = useState(null);

    const [ledgers, setLedger] = useState([]); // default empty array
    const [selectedItem, setSelectedItem] = useState({ master: "", mainGroup: "", subGroup: "" });

    const IST_TZ = "Asia/Kolkata";

    const DATE_FORMATS = ["dd.MM.yy","dd.MM.yyyy","dd-MM-yy","dd-MM-yyyy","yyyy-MM-dd","MM/dd/yyyy"];

    // Parse Excel date to IST format
    const parseCsvDateToIST = (value) => {
        if (!value) return "";

        if (!isNaN(value)) { // Excel serial number
            const excelEpoch = new Date(Date.UTC(1899, 11, 30));
            const date = new Date(excelEpoch.getTime() + value * 86400000);
            const istDate = toZonedTime(date, IST_TZ);
            return format(istDate, "yyyy-MM-dd");
        }

        if (typeof value === "string") {
            for (const fmt of DATE_FORMATS) {
                const parsed = parse(value, fmt, new Date());
                if (isValid(parsed)) {
                    const istDate = toZonedTime(parsed, IST_TZ);
                    return format(istDate, "yyyy-MM-dd");
                }
            }
        }

        return "";
    };

    // Fetch ledgers from API
    const getLedger = async () => {
        try {
            const response = await axios.get("https://api.trucoo.in/ledger/", {
                headers: { "Content-Type": "application/json", "X-API-KEY": "erpTrucoo" },
            });
            if (response.data.status === "success") {
                setLedger(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch ledgers:", error);
        }
    };

    useEffect(() => {
        getLedger();
    }, []);

    // Normalize strings for ledger matching
    const normalize = (str) => str ? str.toString().replace(/[\s\r\n]/g,'').toUpperCase() : '';

    // Map code to ledger
    const getLedgerFromFile = async (code) => {
        if (!ledgers?.length || !code) return null;
        const normCode = normalize(code);

        for (const master of ledgers) {
            for (const main of master?.main ?? []) {
                for (const sub of main?.sub ?? []) {
                    if (normalize(sub?.subDescription) === normCode) {
                        return {
                            master: master?.id,
                            mainGroup: main?.id,
                            subGroup: sub?.id
                        };
                    }
                }
            }
        }
        return null;
    };

    const getLedgerNameFromFile = async (code) => {
        if (!ledgers?.length || !code) return null;
        const normCode = normalize(code);

        for (const master of ledgers) {
            for (const main of master?.main ?? []) {
                for (const sub of main?.sub ?? []) {
                    if (normalize(sub?.subDescription) === normCode) {
                        return sub?.subGroupName;
                    }
                }
            }
        }
        return null;
    };

    // Parse Excel file and map ledger
    const getJson = async (file) => {
        if (!ledgers?.length) {
            alert("Ledger data is still loading. Please wait a moment.");
            return [];
        }

        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        // Find header row starting with "DATE"
        const sheetRange = XLSX.utils.decode_range(sheet['!ref']);
        let headerRowIndex = -1;
        for (let R = sheetRange.s.r; R <= sheetRange.e.r; ++R) {
            const cell = sheet[XLSX.utils.encode_cell({r: R, c: 0})];
            if (cell?.v?.toString().trim().toUpperCase() === 'DATE') {
                headerRowIndex = R;
                break;
            }
        }
        if (headerRowIndex === -1) throw new Error("Header row starting with 'DATE' not found");

        const jsonData = XLSX.utils.sheet_to_json(sheet, { range: headerRowIndex, defval: "" });

        const safeTrim = (v) => v == null ? '' : v.toString().trim();

        const mappedJson = jsonData
            .filter(row => row?.DATE || row?.Date)
            .map(row => ({
                date: parseCsvDateToIST(row['DATE'] ?? row['Date']),
                code: safeTrim(row['PARTY CODE']),
                party: safeTrim(row['PARTY NAME']),
                amount: parseFloat(safeTrim(row['RECEIPT AMOUNT'] ?? row['AMOUNT']).replace(/,/g, '')) || 0,
                acc: safeTrim(row['REF NUM'] ?? ''),
                note: safeTrim(row['NARATTION (NOTE)'] ?? row['NARRATION']),
                ledgerId: safeTrim(row['CODE'] ?? row['Code']),
                ledger: '',
                ledgerName: '',
            }));

        // Populate ledger info
        await Promise.all(mappedJson.map(async (item, index) => {
            item.ledger = await getLedgerFromFile(item.ledgerId);
            if (!item.ledger) console.warn("Ledger not found:", item.ledgerId, "Row:", index);
        }));

        await Promise.all(mappedJson.map(async (item, index) => {
            item.ledgerName = await getLedgerNameFromFile(item.ledgerId);
            if (!item.ledgerName) console.warn("Ledger not found:", item.ledgerId, "Row:", index);
        }));

        // console.log(mappedJson);

        return mappedJson;
    };

    const uploadFile = async (file) => {
        if (!file) return;

        if (!ledgers?.length) {
            console.warn("Ledger data not loaded yet. Upload blocked.");
            return;
        }

        fileNameRef.current.textContent = "Selected: " + file.name;

        try {
            const sendData = await getJson(file);
            setGetData(sendData);
        } catch (err) {
            console.error("Error reading Excel:", err);
            alert("Failed to read file. Please check its format.");
        }
    };

    useEffect(() => {
        const dropArea = dropAreaRef.current;
        const fileInput = fileInputRef.current;
        if (!dropArea || !fileInput) return;

        const openFileDialog = () => !uploading && fileInput.click();
        const onFileChange = () => fileInput.files.length && uploadFile(fileInput.files[0]);
        const onDragOver = (e) => { e.preventDefault(); dropArea.classList.add("hover"); };
        const onDragLeave = () => dropArea.classList.remove("hover");
        const onDrop = (e) => {
            e.preventDefault();
            dropArea.classList.remove("hover");
            if (e.dataTransfer.files.length) uploadFile(e.dataTransfer.files[0]);
        };

        dropArea.addEventListener("click", openFileDialog);
        fileInput.addEventListener("change", onFileChange);
        dropArea.addEventListener("dragover", onDragOver);
        dropArea.addEventListener("dragleave", onDragLeave);
        dropArea.addEventListener("drop", onDrop);

        return () => {
            dropArea.removeEventListener("click", openFileDialog);
            fileInput.removeEventListener("change", onFileChange);
            dropArea.removeEventListener("dragover", onDragOver);
            dropArea.removeEventListener("dragleave", onDragLeave);
            dropArea.removeEventListener("drop", onDrop);
        };
    }, [uploading, ledgers]);

    const checkEmptyFields = (data) => {
        for (let row of data || []) {
            if (!row.date || !row.code || !row.party || !row.amount || !row.ledger) return false;
        }
        return true;
    };

    const uploadData = async () => {
        setUploading(true);

        if (!checkEmptyFields(getData)) {
            alert("Please fill all fields before uploading.");
            setUploading(false);
            return;
        }

        try {
            const response = await axios.post("https://api.trucoo.in/payment/upload/", getData, {
                headers: { 'Content-Type': 'application/json', 'X-API-KEY': 'erpTrucoo' },
            });

            if (response.data.status === "success") {
                alert("Data uploaded successfully!");
                setGetData(null);
            } else {
                alert("Failed to upload data. Please try again.");
            }
        } catch (error) {
            console.error(error);
        }

        setUploading(false);
    };

    // const editData = (index, e) => {
    //     const { name, value } = e.target;
    //     setGetData(prev => prev.map((row, i) => i === index ? { ...row, [name]: value } : row));
    // };

    // const handleSelectLedger = (data) => {
    //     setSelectedItem(data);
    //     setGetData(prev =>
    //         prev.map((row, i) => i === openLedger ? { ...row, ledger: data } : row)
    //     );
    // };

    const handleChange = (index, e) => {
	    const { name, value } = e.target;
	    setGetData(prev =>
            prev.map((row, i) =>
                i === index ? { ...row, [name]: value } : row
            )
        );

		if(name === 'ledgerName'){
			const [masterId, mainGroupId, subGroupId] = value.split(" | ");
			setGetData(prev =>
				prev.map((row, i) =>
					i === index ? { ...row, ['ledgerName']: subGroupId } : row
				)
			);
			ledgers?.forEach(master => {
				if(master.masterName === masterId){
					master.main.forEach(mainGroup => {
						if(mainGroup.mainGroupName === mainGroupId){
							mainGroup.sub.forEach(subGroup => {
								if(subGroup.subGroupName === subGroupId){
									setGetData(prev =>
										prev.map((row, i) =>
											i === index ? { ...row, ['ledger']: {
												master: master.id,
												mainGroup: mainGroup.id,
												subGroup: subGroup.id
											} } : row
										)
									);
								}
							});
						}
					});
				}
			});
		}
	};

    const getLedgerDetails = (data) => {

        if (!data?.master) return "";
        const master = ledgers?.find(l => l.id === data.master);
        if (!master) return "";
        let text = master.masterName;
        if (data.mainGroup) {
            const main = master.main?.find(m => m.id === data.mainGroup);
            text += main ? ` / ${main.mainGroupName}` : "";
            if (data.subGroup) {
                const sub = main?.sub?.find(s => s.id === data.subGroup);
                text += sub ? ` / ${sub.subGroupName}` : "";
            }
        }
        return text;
    };

    return (
        <>
            <Header />
            <Sidebar />

            <div className="content-wrapper">
                <div className="container-full">
                    <section className="content">
                        <div className="col-12">
                            <div className="box">
                                <div className="box-header with-border">
                                    <h4 className="box-title">Payment Receive</h4>
                                    <div align="right" style={{marginTop: '-30px'}}>
                                        <button className='btn btn-warning' onClick={uploadData}>Upload Data</button>
                                    </div>
                                </div>

                                <div className="box-body">
                                    {getData ? (
                                        <div className="table-responsive" style={{width:'100%', height:'calc(100vh - 240px)', overflow:'auto', padding:'0 10px 10px 10px'}}>
                                            <table className="table-sm fs-12 text-fade table-bordered table-hover display nowrap margin-top-10 w-p100">
                                                <thead>
                                                    <tr>
                                                        <th className='text-center'>S.No</th>
                                                        <th>Date</th>
                                                        <th className='text-center'>Code</th>
                                                        <th>Party Name</th>
                                                        <th className='text-end'>Amount</th>
                                                        <th>Ledger</th>
                                                        <th>Ref.No</th>
                                                        <th>Narration</th>
                                                        <th>Edit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getData.map((row, i) => (
                                                        <tr key={i} className={edit===i?'bg-dark':''}>
                                                            <td className='text-center'>{i+1}</td>
                                                            <td>{row.date}</td>
                                                            <td className='text-center'>{row.code}</td>
                                                            <td>{row.party}</td>
                                                            <td className='text-end'>
                                                                {edit===i ? (
                                                                    <input className='form-control text-end' name="amount" value={row.amount} onChange={(e)=>handleChange(i,e)} />
                                                                ) : row.amount}
                                                            </td>
                                                            <td className='fs-10'>
                                                                {edit===i ? (

                                                                    <>
																			<input className="form-control fs-10"  type="text" list="ledgers" placeholder="Select Ledger..." name="ledgerName" value={row?.ledgerName} onChange={(e)=> handleChange(i, e)} />
																			<datalist id="ledgers">
																				{ledgers?.map(ledger=> 
																					ledger?.main?.map(mainGroup =>
																						mainGroup?.sub?.map(subGroup =>
																							<option
																								key={subGroup.id}
																								value={`${ledger.masterName} | ${mainGroup.mainGroupName} | ${subGroup.subGroupName}`}
																							>
																								{subGroup.subGroupName}
																							</option>
																						)
																					)
																				)}
																			</datalist>
																		</>
                                                                    // openLedger===i ? (
                                                                    //     <div className="popup-overlay">
                                                                    //         <div className="popup">
                                                                    //             <h4>Select Ledger</h4>
                                                                    //             {ledgers.length>0 && (
                                                                    //                 <NestedAccordion
                                                                    //                     selectedItem={selectedItem}
                                                                    //                     onSelect={handleSelectLedger}
                                                                    //                     ledgers={ledgers}
                                                                    //                     close={setOpenLedger}
                                                                    //                 />
                                                                    //             )}
                                                                    //         </div>
                                                                    //     </div>
                                                                    // ) : (
                                                                    //     <button className='btn btn-sm btn-warning' onClick={()=>setOpenLedger(i)}>
                                                                    //         {getLedgerDetails(row.ledger) || 'Select Ledger'}
                                                                    //     </button>
                                                                    // )

                                                                ) : row.ledgerName}
                                                            </td>
                                                            <td>
                                                                {edit===i ? (
                                                                    <input  className='form-control text-end' name="acc" value={row.acc} onChange={(e)=>handleChange(i,e)} />
                                                                ) : row.acc}
                                                            </td>
                                                            <td>
                                                                {edit===i ? (
                                                                    <input className='form-control text-end' name="note" value={row.note} onChange={(e)=>handleChange(i,e)} />
                                                                ) : row.note}
                                                            </td>
                                                            <td>
                                                                {edit===i ? (
                                                                    <button className='btn btn-sm btn-primary' onClick={()=>setEdit(null)}>Save</button>
                                                                ) : (
                                                                    <button className='btn btn-sm btn-danger' onClick={()=>setEdit(i)}>Edit</button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div style={{display:'flex', justifyContent:'center', minHeight:'65vh', alignItems:'center', flexDirection:'column'}}>
                                            <h4 className='mb-4'>Upload file</h4>
                                            <form ref={formRef}>
                                                <div className={`drop-area ${!ledgers.length ? 'loading':''}`} ref={dropAreaRef}>
                                                    <p>Drag & Drop Excel File Here</p>
                                                    <p>or Click to Select</p>
                                                    <input type="file" id="fileInput" ref={fileInputRef} accept=".xlsx" disabled={!ledgers.length} />
                                                    {!ledgers.length && <p style={{color:'red'}}>Loading ledger data, please wait...</p>}
                                                </div>
                                                <div ref={fileNameRef} style={{display:'flex', justifyContent:'center'}}></div>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
}

export default App;
